import { useEffect, useRef, useState } from 'react';

/**
 * Copia `text` al portapapeles con feedback breve.
 *
 * Ojo con el DOM: este botón se usa como hermano suelto del <label> del campo
 * (no envuelto junto a él), porque los e2e resuelven cada control con
 * //label[...]/.. y después buscan input/textarea/select dentro de ese padre.
 * Un <button> no matchea ese selector, así que puede convivir ahí sin romperlos.
 * El posicionamiento lo hace `.field-copy` en styles.css.
 */
export default function CopyButton({ text, label = 'Copiar' }: { text: string; label?: string }) {
  const [state, setState] = useState<'idle' | 'ok' | 'error'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (next: 'ok' | 'error') => {
    setState(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), next === 'ok' ? 1500 : 2500);
  };

  const copy = async () => {
    const value = text ?? '';
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback para contexto no seguro (http) o navegadores viejos.
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand copy devolvió false');
      }
      flash('ok');
    } catch {
      // El navegador puede bloquear el portapapeles (permiso denegado, contexto
      // no seguro). No rompemos el formulario, pero tampoco dejamos el botón
      // mudo: sin aviso parece que no hizo nada.
      flash('error');
    }
  };

  return (
    <button
      type="button"
      className="ghost sm"
      onClick={copy}
      disabled={!text}
      title={text ? 'Copiar al portapapeles' : 'Nada para copiar'}
    >
      {state === 'ok' ? 'Copiado ✓' : state === 'error' ? 'No se pudo copiar' : label}
    </button>
  );
}
